/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import React from 'react';
import { useState, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { BiTrash } from 'react-icons/bi';
import { format, isToday, isYesterday } from 'date-fns';
import MessageStatuses, { StatusesEnum } from './MessageStatuses';
import { Sender } from '../IndividualChatBodyNew';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { DeleteMessageContainer, StyledReferenceLink } from './MessageItem.styled';
import { useWindowSize } from 'hooks/useWindowSize';
import DeleteChatMessageConfirmationModal from '../../DeleteChatMessageConfirmationModal';
import api from 'api';
import { ChatReference } from 'api/rest/chat_new';
import { TicketInterface } from 'types';
import { setAlert } from 'redux/alert';
import ViewTicket from 'views/v2/Dashboards-B2B/components/backOfficeFlow/tickets/components/viewTicket';
import DocumentPreview from 'components/v2/common/documentPreview';
import { ACTIONS } from 'constant';

import {
    StyledMessageAvatar,
    StyledMessageBubble,
    StyledMessageItem,
    StyledMessageItemDate,
    StyledMessageTime,
} from './MessageItem.styled';

import LogoExample from 'assets/icons/LogoExample.png';

interface Props {
    showItemDate?: boolean;
    status?: StatusesEnum;
    message?: string;
    id?: string;
    sender: Sender;
    time: string;
    avatar?: any;
    author: string;
    references?: ChatReference[];
}

export default function MessageItem(props: Props) {
    const dispatch = useDispatch<AppDispatch>();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const {
        status = StatusesEnum.DELIVERED,
        sender = Sender.OTHER,
        id = '',
        message = '',
        avatar,
        time,
        author,
        references,
    } = props;
    const [hovered, setHovered] = useState(false);
    const [localMessage, setLocalMessage] = useState(message);
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const [deleteChatMessageConfirmationModalOpen, setDeleteChatMessageConfirmationModalOpenClose] =
        useState<boolean>(false);
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const [viewTicket, setViewTicket] = useState<TicketInterface | null>(null);
    const [previewFile, setPreviewFile] = useState<{ fileUrl: string; fileName: string } | null>(
        null
    );

    const handleReferenceClick = async (ref: ChatReference) => {
        if (ref.type === 'ticket') {
            try {
                const resp = await api.tickets.getTicket(ref.id.toString());
                setViewTicket(resp.data as TicketInterface);
            } catch {
                dispatch(setAlert({ message: 'Failed to load ticket', type: 'error' }));
            }
        } else if (ref.type === 'document') {
            if (!activeApp?.id) return;
            try {
                const resp = await api.documentResource.getDocumentPreviewUrl(
                    activeApp.id.toString(),
                    ref.id
                );
                setPreviewFile({
                    fileUrl: resp.data.url,
                    fileName: ref.name || `Document #${ref.id}`,
                });
            } catch {
                dispatch(setAlert({ message: 'Failed to load document', type: 'error' }));
            }
        }
    };

    const handleViewTicketAction = (action: ACTIONS, ticket: TicketInterface) => {
        if (action === ACTIONS.PREVIEW_FILE && activeApp?.id) {
            (async () => {
                try {
                    const resp = await api.documentResource.getDocumentPreviewUrl(
                        activeApp.id!.toString(),
                        Number(ticket.document_id)
                    );
                    setPreviewFile({
                        fileUrl: resp.data.url,
                        fileName: ticket.document_name || 'Document',
                    });
                } catch {
                    dispatch(setAlert({ message: 'Failed to load document', type: 'error' }));
                }
            })();
        }
        setViewTicket(null);
    };

    const deletedMessage = localMessage === t('deleted', { lng: language });
    const formattedMessage = `${author}:\n${localMessage}`;
    const timeAsDate = new Date(time);
    const now = new Date();
    const timeAsDatePlus30 = new Date(timeAsDate.getTime() + 30 * 60000);
    const messageIsRecent = now <= timeAsDatePlus30;
    const canDeleteMessage =
        !deletedMessage && sender === Sender.ME && (isMobile || hovered) && messageIsRecent;

    const handleDeleteMessage = async (messageId: number) => {
        try {
            await api.chatNew.update_message(messageId, t('deleted', { lng: language }));
            setLocalMessage(t('deleted', { lng: language }));
        } catch (error) {
            console.error('Error deleting message:', error);
        }
    };

    const handleHover = () => {
        setHovered(!hovered);
    };

    const handleOpenCloseDeleteDocModal = () => {
        setDeleteChatMessageConfirmationModalOpenClose((state) => !state);
    };

    function formatDate(date: Date) {
        if (date) {
            const month = date.toLocaleString(language === 'EN' ? 'default' : 'fr', {
                month: 'short',
            });
            const day = date.getDate();
            const year = date.getFullYear();
            const formattedDate = `${month.toUpperCase()} ${day}, ${year}`;
            return formattedDate;
        } else {
            return '';
        }
    }

    const messagesDate = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (isToday(new Date(time))) {
            return t('today', { lng: language }).toUpperCase();
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
        } else if (isYesterday(new Date(time))) {
            return t('yesterday', { lng: language }).toUpperCase();
        } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const date = new Date(time);
            return formatDate(date);
        }
    };

    const handleTimeValue = (timeValue: string | undefined) => {
        if (timeValue) {
            return format(new Date(timeValue), 'HH:mm');
        }
        return '';
    };

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    return (
        <>
            {props.showItemDate && <StyledMessageItemDate>{messagesDate()}</StyledMessageItemDate>}
            <StyledMessageItem
                sender={sender}
                onMouseEnter={handleHover}
                onMouseLeave={handleHover}
            >
                <DeleteMessageContainer
                    show={canDeleteMessage}
                    onClick={handleOpenCloseDeleteDocModal}
                >
                    <BiTrash style={{ width: '20px', height: '20px' }} />
                </DeleteMessageContainer>

                {avatar ? (
                    <StyledMessageAvatar src={avatar.default} alt="" />
                ) : sender !== Sender.ME ? (
                    <StyledMessageAvatar src={LogoExample} alt="" />
                ) : (
                    <MessageStatuses status={status} />
                )}

                <StyledMessageBubble deleted={deletedMessage} sender={sender}>
                    {references &&
                        references.length > 0 &&
                        references.map((ref, i) => (
                            <React.Fragment key={i}>
                                {'Re: '}
                                <StyledReferenceLink
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleReferenceClick(ref);
                                    }}
                                >
                                    {ref.name || `${ref.type} #${ref.id}`}
                                </StyledReferenceLink>
                                <br />
                            </React.Fragment>
                        ))}
                    {formattedMessage.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                            {line}
                            <br />
                        </React.Fragment>
                    ))}
                </StyledMessageBubble>
                <StyledMessageTime>{handleTimeValue(time)}</StyledMessageTime>
            </StyledMessageItem>

            {id && (
                <DeleteChatMessageConfirmationModal
                    toggleDeleteChatMessageModal={handleOpenCloseDeleteDocModal}
                    handleDeleteChatMessage={handleDeleteMessage}
                    messageId={Number(id)}
                    open={deleteChatMessageConfirmationModalOpen}
                    mobile={mobile}
                />
            )}

            {viewTicket && (
                <ViewTicket
                    ticket={viewTicket}
                    onClose={() => setViewTicket(null)}
                    handleAction={handleViewTicketAction}
                />
            )}

            {previewFile && (
                <DocumentPreview
                    open
                    mobile={mobile}
                    onClose={() => setPreviewFile(null)}
                    title="Document preview"
                    fileUrl={previewFile.fileUrl}
                    fileName={previewFile.fileName}
                />
            )}
        </>
    );
}
